/**
 * Whether some other machine on the internet could fetch this URL.
 *
 * Fetch-based media delivery (Cloudinary's `image/fetch`, and anything else that pulls an
 * asset from its origin rather than being handed the bytes) only works when the origin is
 * reachable from that service. A URL only this browser can resolve — a dev API on
 * localhost, a service name inside a compose network, an address on a private range — is
 * not a valid input: the request fails at the far end and the page renders a broken image,
 * with the real cause invisible from here.
 *
 * Decided per URL rather than from `environment.local`, because the question is whether
 * another machine can reach the host, and that does not line up with which build is
 * running. A staging API behind a private load balancer is just as unreachable from a
 * production build, and a dev machine pointed at a public API is perfectly reachable.
 *
 * Anything unparseable is treated as fetchable, so this can only ever drop a request that
 * was going to fail — it never suppresses one that would have worked.
 */
export function isPubliclyFetchableUrl(url?: string): boolean {
	let host: string;

	try {
		// `new URL` keeps the brackets around an IPv6 literal; the checks below want it bare.
		host = new URL(url ?? '').hostname.toLowerCase().replace(/^\[|\]$/g, '');
	} catch {
		// Relative paths, empty strings and malformed input land here. They are the caller's
		// existing problem, not this one's, so leave their handling unchanged.
		return true;
	}

	if (!host) {
		return true;
	}

	if (host.includes(':')) {
		return isPublicIpv6(host);
	}

	return !isLoopbackName(host) && !isPrivateIpv4(host) && !isNonPublicName(host);
}

function isLoopbackName(host: string): boolean {
	return host === 'localhost' || /^127\./.test(host);
}

/** The IPv4 ranges reserved for private networks, link-local addressing and "this host". */
function isPrivateIpv4(host: string): boolean {
	if (host === '0.0.0.0') {
		return true;
	}

	if (/^10\./.test(host) || /^192\.168\./.test(host) || /^169\.254\./.test(host)) {
		return true;
	}

	// 172.16.0.0/12 — only the second octet decides, so the other 172.x.x.x are public.
	const match = /^172\.(\d{1,3})\./.exec(host);
	return !!match && Number(match[1]) >= 16 && Number(match[1]) <= 31;
}

function isPublicIpv6(host: string): boolean {
	if (host === '::1' || host === '::') {
		return false;
	}

	// Unique-local (fc00::/7) and link-local (fe80::/10). Matched only here, where the host
	// is known to be an address — as a name prefix, `fc`/`fe8` is perfectly ordinary.
	return !/^f[cd]/.test(host) && !/^fe[89ab]/.test(host);
}

/**
 * Names that do not resolve outside their own network: the two suffixes a local setup
 * actually uses, and any dotless hostname, which only means something inside its own network
 * (a container or compose service such as `http://api:3000`).
 *
 * Kept deliberately short. Other reserved suffixes exist, but in this codebase they turn up
 * only in test fixtures standing in for public hosts, where treating them as unreachable
 * would change behaviour without fixing anything real.
 */
function isNonPublicName(host: string): boolean {
	if (/\.(localhost|local)$/.test(host)) {
		return true;
	}

	return !host.includes('.');
}
